import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import type { User, UserConnectionsView, UserLastSeenAt } from "@/api/types"
import { logoutAction } from "@/store/session/logoutAction"
import type { RootState } from "@/store/store"

interface UsersState {
  users: Record<string, User>
  notFound: Record<string, boolean>
  loading: Record<string, boolean>
  lastSeenAt: Record<string, number>
  lastSeenFetchedAt: Record<string, number>
  userNotes: Record<string, string>
  userConnections: Record<string, UserConnectionsView>
}

const initialState: UsersState = {
  users: {},
  notFound: {},
  loading: {},
  lastSeenAt: {},
  lastSeenFetchedAt: {},
  userNotes: {},
  userConnections: {},
}

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[]>) => {
      action.payload.forEach((user) => {
        state.users[user.id] = user
        delete state.notFound[user.id]
        delete state.loading[user.id]
      })
    },
    markUsersNotFound: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((userId) => {
        state.notFound[userId] = true
        delete state.loading[userId]
      })
    },
    setUsersLoading: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((userId) => {
        state.loading[userId] = true
      })
    },
    clearUsersLoading: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((userId) => {
        delete state.loading[userId]
      })
    },
    setUsersLastSeenAt: (
      state,
      action: PayloadAction<{
        entries: UserLastSeenAt[]
        fetchedIds: string[]
      }>,
    ) => {
      const now = Date.now()

      action.payload.entries.forEach(({ userId, lastSeenAt }) => {
        state.lastSeenAt[userId] = lastSeenAt
      })

      action.payload.fetchedIds.forEach((userId) => {
        state.lastSeenFetchedAt[userId] = now
      })
    },
    setUserNote: (
      state,
      action: PayloadAction<{
        userId: string
        note: string
      }>,
    ) => {
      state.userNotes[action.payload.userId] = action.payload.note
    },
    setUserConnections: (
      state,
      action: PayloadAction<{
        userId: string
        connections: UserConnectionsView
      }>,
    ) => {
      state.userConnections[action.payload.userId] = action.payload.connections
    },
    resetUserConnections: (state) => {
      state.userConnections = {}
    },
    resetUsers: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  setUsers,
  markUsersNotFound,
  setUsersLoading,
  clearUsersLoading,
  setUsersLastSeenAt,
  setUserNote,
  setUserConnections,
  resetUserConnections,
  resetUsers,
} = usersSlice.actions

export const selectUsers = (state: RootState) => state.participantUsers.users
export const selectUsersNotFound = (state: RootState) =>
  state.participantUsers.notFound
export const selectUsersLoading = (state: RootState) =>
  state.participantUsers.loading
export const selectUsersLastSeenAt = (state: RootState) =>
  state.participantUsers.lastSeenAt
export const selectUsersLastSeenFetchedAt = (state: RootState) =>
  state.participantUsers.lastSeenFetchedAt
export const selectUserNotes = (state: RootState) =>
  state.participantUsers.userNotes
export const selectUserConnections = (state: RootState) =>
  state.participantUsers.userConnections

export default usersSlice.reducer
